import * as React from 'react';
import strings from '../../../strings/strings.json';
import { FlexColMd, FlexEnd, FlexSmAlignCenter } from '../../../components/Elements';
import { ErrorMessage } from '../../../components/forms/FormComponents';
import RadioFields, { RadioFieldOption } from '../../../components/forms/RadioFields';
import useNeveForm from 'components/forms/NeveForm';
import { styled } from 'styled-components';
import { sizes } from 'theme';
import { ContentComponent } from './stepComponents/CourseStructureAndContentComponent';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import Button from 'components/buttons/Button';

enum ActivityType {
    TASK_TEXT_AND_IMAGE = 0,
    TASK_VIDEO = 1,
    TASK_AUDIO = 2,
    QUESTIONNAIRE = 3,
}

type ActivityTypeOption = {
    activityType: ActivityType;
    name: string;
    description: string;
    aria: string;
    action: () => void;
};

type ActivityTypeInput = {
    type: 0 | 1 | 2 | 3 | null;
};

type ActivityTypeSelectionPageContentProps = {
    moduleId: string;
    setComponent: React.Dispatch<React.SetStateAction<ContentComponent>>;
};

export default function ActivityTypeSelectionForm({
    moduleId,
    setComponent,
}: ActivityTypeSelectionPageContentProps): JSX.Element {
    const { editMode } = React.useContext(BuildCourseContext);

    const { register, handleSubmit } = useNeveForm<ActivityTypeInput>({
        type: null,
    });

    const [error, setError] = React.useState<string | null>(null);

    const ACTIVITY_TYPE_OPTIONS: ActivityTypeOption[] = [
        {
            activityType: ActivityType.TASK_TEXT_AND_IMAGE,
            name: strings.activityTypeSelectionPage.taskTextAndImageName,
            description: strings.activityTypeSelectionPage.taskTextAndImageDescription,
            aria: strings.activityTypeSelectionPage.taskTextAndImageAria,
            action: () => {
                setComponent({ name: 'addTaskText', moduleId: moduleId });
            },
        },
        {
            activityType: ActivityType.TASK_VIDEO,
            name: strings.activityTypeSelectionPage.taskVideoName,
            description: strings.activityTypeSelectionPage.taskVideoDescription,
            aria: strings.activityTypeSelectionPage.taskVideoAria,
            action: () => {
                setComponent({ name: 'addTaskVideo', moduleId: moduleId });
            },
        },
        {
            activityType: ActivityType.TASK_AUDIO,
            name: strings.activityTypeSelectionPage.taskAudioName,
            description: strings.activityTypeSelectionPage.taskAudioDescription,
            aria: strings.activityTypeSelectionPage.taskAudioAria,
            action: () => {
                setComponent({ name: 'addTaskAudio', moduleId: moduleId });
            },
        },
    ];

    if (!editMode) {
        ACTIVITY_TYPE_OPTIONS.push({
            activityType: ActivityType.QUESTIONNAIRE,
            name: strings.activityTypeSelectionPage.questionnaireName,
            description: strings.activityTypeSelectionPage.questionnaireDescription,
            aria: strings.activityTypeSelectionPage.questionnaireAria,
            action: () => {
                setComponent({ name: 'addQuestionnaire', moduleId: moduleId });
            },
        });
    }

    const activityTypeOptions: RadioFieldOption[] = ACTIVITY_TYPE_OPTIONS.map((activityType) => {
        return {
            value: activityType.activityType,
            title: activityType.name,
            description: activityType.description,
            aria: activityType.aria,
            inputProps: register('type'),
            name: 'type',
        };
    });

    const submitActivityType = (formData: ActivityTypeInput) => {
        if (formData.type === null) {
            setError(strings.activityTypeSelectionPage.typeValidationError);
            return;
        }

        const selectedAction = ACTIVITY_TYPE_OPTIONS.find(
            (x) => x.activityType === (formData.type === null ? 0 : +formData?.type),
        )?.action;

        if (selectedAction) {
            selectedAction();
        }
    };

    return (
        <Container onSubmit={handleSubmit(submitActivityType)}>
            <FlexColMd>
                <RadioFields options={activityTypeOptions} autoFocus />
                <FlexEnd>
                    <FlexSmAlignCenter>
                        {error && <ErrorMessage id={`error-message`}>{error}</ErrorMessage>}
                        <Button
                            type="submit"
                            title={strings.activityTypeSelectionPage.submitButton}
                            aria={strings.activityTypeSelectionPage.submitButtonAria}
                        />
                    </FlexSmAlignCenter>
                </FlexEnd>
            </FlexColMd>
        </Container>
    );
}

const Container = styled.form`
    padding: 0 ${sizes.spacingSm};
`;
